import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { addVendorPaymentValidationSchema } from '../../../validations/SocietyPortal/VendorValidation';
import { getAllVendors, updateVendorPayment, getVendorUnpaidBills, getVendorPaymentDetails } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import * as routes from '../../../Routing/routes';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { PAYMENT_TYPES, toFormPaymentType } from '../../../utils/helpers/paymentType';
import { formatIstDate } from '../../../utils/helpers/istDate';

const TDS_SECTIONS = ['194C', '194J', '194I', '194H', '194A', '194Q'];

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'neft', label: 'NEFT' },
];

const EditVendorPayment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    allVendorsData, allVendorsLoading,
    updateVendorPaymentLoading,
    vendorUnpaidBillsData, vendorUnpaidBillsLoading,
    vendorPaymentDetailsData, vendorPaymentDetailsLoading,
  } = useSelector(state => state.VendorReducers);

  const [billPayments, setBillPayments] = useState([]);
  const [documentFile, setDocumentFile] = useState(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    dispatch(getAllVendors({ society_id: selectedSocietyStorer.getSocietyId() }));
    dispatch(getVendorPaymentDetails({ paymentId: id }));
  }, [dispatch, id]);

  // Fetch unpaid bills when payment details load
  useEffect(() => {
    if (vendorPaymentDetailsData?.vendor_id) {
      dispatch(getVendorUnpaidBills({
        vendor_id: vendorPaymentDetailsData.vendor_id,
        societyId: selectedSocietyStorer.getSocietyId(),
      }));
    }
  }, [dispatch, vendorPaymentDetailsData?.vendor_id]);

  const vendorsList = useMemo(() => {
    if (!Array.isArray(allVendorsData)) return [];
    return allVendorsData.map(vendor => ({
      id: vendor.vendor_id,
      name: vendor.business_name,
      glCode: vendor.vendor_gl_code || '',
      tdsApplicable: vendor.tds_applicable || 'N',
      tdsRate: parseFloat(vendor.tds_rate) || 0,
      tdsSection: vendor.tds_section || '',
    }));
  }, [allVendorsData]);

  const unpaidBills = useMemo(() => {
    const bills = Array.isArray(vendorUnpaidBillsData)
      ? vendorUnpaidBillsData
      : vendorUnpaidBillsData?.bills || [];
    const mapped = bills.map(bill => ({
      id: bill.bill_id || bill.id,
      billDate: bill.bill_date,
      billDueDate: bill.due_date,
      billNumber: bill.bill_number,
      billAmount: parseFloat(bill.total_amount || bill.netamount || bill.total || 0),
      amountDue: parseFloat(bill.balance_amount || bill.total_amount || 0),
    }));

    // Merge bills from existing payment details that may not appear in unpaid list
    const existingDetails = vendorPaymentDetailsData?.details || [];
    existingDetails.forEach(det => {
      const billId = det.bill_id;
      if (billId && !mapped.find(b => b.id === billId) && det.bill) {
        mapped.push({
          id: billId,
          billDate: det.bill.bill_date,
          billDueDate: det.bill.due_date,
          billNumber: det.bill.bill_number,
          billAmount: parseFloat(det.bill.total_amount || 0),
          amountDue: parseFloat(det.bill.balance_amount || 0) + parseFloat(det.amount || 0),
        });
      }
    });

    return mapped;
  }, [vendorUnpaidBillsData, vendorPaymentDetailsData]);

  // Pre-populate bill payments from existing payment details
  useEffect(() => {
    if (vendorPaymentDetailsData?.details && !initialized && unpaidBills.length > 0) {
      const existingPayments = vendorPaymentDetailsData.details
        .filter(d => d.amount > 0)
        .map(d => ({
          billId: d.bill_id,
          payment: parseFloat(d.amount) || 0,
        }));
      setBillPayments(existingPayments);
      setInitialized(true);
    }
  }, [vendorPaymentDetailsData, initialized, unpaidBills]);

  const handleVendorChange = useCallback((vendorId, setFieldValue) => {
    setFieldValue('vendorId', vendorId);
    setBillPayments([]);
    if (vendorId) {
      dispatch(getVendorUnpaidBills({
        vendor_id: vendorId,
        societyId: selectedSocietyStorer.getSocietyId(),
      }));
    }
  }, [dispatch]);

  const handleBillPaymentChange = useCallback((billId, value) => {
    const amount = parseFloat(value) || 0;
    setBillPayments(prev => {
      const existing = prev.find(bp => bp.billId === billId);
      if (existing) {
        if (amount === 0) return prev.filter(bp => bp.billId !== billId);
        return prev.map(bp => bp.billId === billId ? { ...bp, payment: amount } : bp);
      }
      if (amount > 0) return [...prev, { billId, payment: amount }];
      return prev;
    });
  }, []);

  const totalBillPayment = useMemo(() => {
    return billPayments.reduce((sum, bp) => sum + bp.payment, 0);
  }, [billPayments]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount);
  };

  // Build initial values from payment details
  const initialValues = useMemo(() => {
    const p = vendorPaymentDetailsData;
    if (!p) return {
      vendorId: '', paymentType: '', paymentDate: '', amount: '', paymentMode: '', referenceNo: '', notes: '',
      tdsApplicable: false, tdsRate: '', tdsSection: '',
    };
    return {
      vendorId: String(p.vendor_id || ''),
      paymentType: toFormPaymentType(p.payment_type),
      paymentDate: p.payment_date || '',
      amount: p.payment_amount || '',
      paymentMode: p.payment_mode || '',
      referenceNo: p.reference || '',
      notes: p.notes || '',
      tdsApplicable: p.tds_applicable === 'Y',
      tdsRate: p.tds_rate != null ? parseFloat(p.tds_rate) : '',
      tdsSection: p.tds_section || '',
    };
  }, [vendorPaymentDetailsData]);

  const handleSubmit = (values) => {
    const selectedVendor = vendorsList.find(v => String(v.id) === String(values.vendorId));
    const paymentMade = parseFloat(values.amount) || 0;
    const tdsApplicable = values.tdsApplicable ? 'Y' : 'N';
    const tdsRate = parseFloat(values.tdsRate) || 0;
    const tdsAmount = tdsApplicable === 'Y' ? +((paymentMade * tdsRate) / 100).toFixed(2) : 0;
    const amountAfterTds = +(paymentMade - tdsAmount).toFixed(2);

    const billsWithPayments = unpaidBills
      .map(bill => {
        const bp = billPayments.find(bp => bp.billId === bill.id);
        return {
          id: bill.id,
          date: bill.billDate,
          dueDate: bill.billDueDate,
          billNo: bill.billNumber,
          billAmount: bill.billAmount,
          amountDue: bill.amountDue,
          payment: bp?.payment || 0,
        };
      })
      .filter(bill => bill.payment > 0);

    dispatch(updateVendorPayment({
      paymentId: id,
      societyId: selectedSocietyStorer.getSocietyId(),
      vendorId: values.vendorId,
      vendorName: selectedVendor?.name || '',
      vendorGlCode: selectedVendor?.glCode || '',
      paymentType: values.paymentType,
      paymentDate: values.paymentDate,
      paymentMade: paymentMade,
      paymentMode: values.paymentMode,
      utrNo: values.referenceNo || '',
      paymentDoc: documentFile || null,
      notes: values.notes || '',
      amountUsedForPayments: totalBillPayment,
      dueAmount: paymentMade - totalBillPayment,
      tdsApplicable: tdsApplicable,
      tdsRate: tdsRate,
      tdsSection: values.tdsSection || '',
      tdsAmount: tdsAmount,
      amountAfterTds: amountAfterTds,
      bills: billsWithPayments,
      onSuccess: () => {
        navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS);
      },
    }));
  };

  if (vendorPaymentDetailsLoading) {
    return (
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
        <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
        <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading payment details...</p>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
        <button
          onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS)}
          className="tw-p-1.5 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500"
        >
          <Icon icon="mdi:arrow-left" className="tw-text-lg" />
        </button>
        <div>
          <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Edit Vendor Payment</h1>
          <p className="tw-text-xs tw-text-gray-500">
            {vendorPaymentDetailsData?.payment_no || 'Update payment details'}
          </p>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={addVendorPaymentValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div className="tw-grid tw-grid-cols-12 tw-gap-4">
              {/* Left: Payment Form */}
              <div className="tw-col-span-8">
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                  <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">Payment Details</h2>

                  <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3">
                    {/* Vendor Name */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Vendor Name *</label>
                      <Field
                        as="select"
                        name="vendorId"
                        onChange={(e) => handleVendorChange(e.target.value, setFieldValue)}
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                        disabled={allVendorsLoading}
                      >
                        <option value="">Select vendor</option>
                        {vendorsList.map(v => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="vendorId" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>

                    {/* Payment Type */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Type *</label>
                      <Field
                        as="select"
                        name="paymentType"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                      >
                        <option value="">Select type</option>
                        {PAYMENT_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="paymentType" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>
                  </div>

                  <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3">
                    {/* Payment Date */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Date *</label>
                      <Field
                        type="date"
                        name="paymentDate"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                      />
                      <ErrorMessage name="paymentDate" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>

                    {/* Payment Made (Amount) */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Made *</label>
                      <Field
                        type="number"
                        name="amount"
                        placeholder="0.00"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                      />
                      <ErrorMessage name="amount" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>
                  </div>

                  <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3">
                    {/* Payment Mode */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Mode *</label>
                      <Field
                        as="select"
                        name="paymentMode"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                      >
                        <option value="">Select mode</option>
                        {PAYMENT_MODES.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="paymentMode" component="p" className="tw-text-red-500 tw-text-[10px] tw-mt-0.5" />
                    </div>

                    {/* UTR/Cheque No */}
                    <div>
                      <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">UTR/Cheque No.</label>
                      <Field
                        type="text"
                        name="referenceNo"
                        placeholder="Enter UTR or Cheque number"
                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                      />
                    </div>
                  </div>

                  {/* TDS — hydrated from the payment record so an imported deduction is
                      visible and editable instead of being silently reset on save. */}
                  <div className="tw-mb-3 tw-border tw-border-orange-100 tw-bg-orange-50/50 tw-rounded-lg tw-p-3">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon="mdi:file-percent" className="tw-text-orange-500" />
                        TDS Deduction
                      </h3>
                      <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                        <input
                          type="checkbox"
                          checked={values.tdsApplicable}
                          onChange={(e) => setFieldValue('tdsApplicable', e.target.checked)}
                          className="tw-rounded tw-border-gray-300"
                        />
                        Deduct TDS on this payment
                      </label>
                    </div>
                    {values.tdsApplicable && (
                      <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                        <div>
                          <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TDS Rate (%)</label>
                          <Field
                            type="number"
                            name="tdsRate"
                            min="0"
                            max="100"
                            step="0.01"
                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TDS Section</label>
                          <Field
                            as="select"
                            name="tdsSection"
                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                          >
                            <option value="">Select section</option>
                            {TDS_SECTIONS.map(sec => (
                              <option key={sec} value={sec}>{sec}</option>
                            ))}
                          </Field>
                        </div>
                        <div>
                          <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TDS Amount</label>
                          <div className="tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-gray-50 tw-text-gray-700">
                            {formatCurrency(((parseFloat(values.amount) || 0) * (parseFloat(values.tdsRate) || 0)) / 100)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Upload Document */}
                  <div className="tw-mb-3">
                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Upload Document</label>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="file"
                        onChange={(e) => setDocumentFile(e.target.files[0] || null)}
                        className="tw-w-full tw-px-3 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      />
                      {documentFile && (
                        <button
                          type="button"
                          onClick={() => setDocumentFile(null)}
                          className="tw-p-1 tw-text-red-400 hover:tw-text-red-600"
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      )}
                    </div>
                    {vendorPaymentDetailsData?.attachment && !documentFile && (
                      <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                        Current: {vendorPaymentDetailsData.attachment}
                      </p>
                    )}
                  </div>

                  {/* Bills to Pay Table — hidden for advance payments (they
                      aren't tied to any bill). Allocation is always optional:
                      the payment saves even if nothing is allocated. */}
                  {values.vendorId && values.paymentType === 'bill_payment' && (
                    <div className="tw-mt-4">
                      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-1.5">
                          <Icon icon="mdi:file-document-outline" className="tw-text-blue-500" />
                          Bills to Pay <span className="tw-text-gray-400 tw-font-normal tw-text-xs">(Optional)</span>
                        </h3>
                        <span className="tw-text-[11px] tw-text-gray-500">
                          Leave amounts blank to record as a general payment.
                        </span>
                      </div>
                      {vendorUnpaidBillsLoading ? (
                        <div className="tw-text-center tw-py-4">
                          <Icon icon="mdi:loading" className="tw-animate-spin tw-text-xl tw-text-blue-500" />
                          <p className="tw-text-xs tw-text-gray-500 tw-mt-1">Loading bills...</p>
                        </div>
                      ) : unpaidBills.length === 0 ? (
                        <div className="tw-text-center tw-py-4 tw-bg-gray-50 tw-rounded-lg">
                          <Icon icon="mdi:file-document-remove-outline" className="tw-text-2xl tw-text-gray-300" />
                          <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                            No unpaid bills found for this vendor — save the payment to record it as a general payment.
                          </p>
                        </div>
                      ) : (
                        <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                          <table className="tw-w-full tw-text-sm">
                            <thead>
                              <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Bill Date</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Bill No#</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Bill Amount</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase">Amount Due</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-w-[140px]">Payment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {unpaidBills.map((bill) => {
                                const currentPayment = billPayments.find(bp => bp.billId === bill.id)?.payment || '';
                                return (
                                  <tr key={bill.id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50">
                                    <td className="tw-px-3 tw-py-2">
                                      <p className="tw-text-gray-600">{bill.billDate ? formatIstDate(bill.billDate) : '-'}</p>
                                      {bill.billDueDate && <p className="tw-text-[10px] tw-text-gray-400">Due: {formatIstDate(bill.billDueDate)}</p>}
                                    </td>
                                    <td className="tw-px-3 tw-py-2">
                                      <span className="tw-font-medium tw-text-blue-600">{bill.billNumber}</span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-800">
                                      {formatCurrency(bill.billAmount)}
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-red-600">
                                      {formatCurrency(bill.amountDue)}
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-right">
                                      <input
                                        type="number"
                                        value={currentPayment}
                                        onChange={(e) => handleBillPaymentChange(bill.id, e.target.value)}
                                        max={bill.amountDue}
                                        min={0}
                                        step="0.01"
                                        placeholder="0.00"
                                        className="tw-w-[120px] tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded tw-text-sm tw-text-right focus:tw-outline-none focus:tw-border-blue-500"
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  <div className="tw-mt-3">
                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                      Notes <span className="tw-text-gray-400">(Internal use. Not visible to vendor)</span>
                    </label>
                    <Field
                      as="textarea"
                      name="notes"
                      rows="2"
                      placeholder="Add internal notes..."
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Right: Payment Summary */}
              <div className="tw-col-span-4">
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-sticky tw-top-4">
                  <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4">Payment Summary</h2>

                  <div className="tw-space-y-3">
                    <div className="tw-flex tw-justify-between tw-text-sm">
                      <span className="tw-text-gray-500">Amount Paid:</span>
                      <span className="tw-font-semibold tw-text-gray-800">
                        {formatCurrency(parseFloat(values.amount) || 0)}
                      </span>
                    </div>

                    {/* TDS lines, as on Add Payment. Without them the summary
                        showed only the gross amount, so a payment with TDS looked
                        like the vendor was paid the full figure. */}
                    {values.tdsApplicable && (
                      <>
                        <div className="tw-flex tw-justify-between tw-text-sm">
                          <span className="tw-text-gray-500">TDS ({parseFloat(values.tdsRate) || 0}%{values.tdsSection ? ` · ${values.tdsSection}` : ''}):</span>
                          <span className="tw-font-semibold tw-text-orange-600">
                            -{formatCurrency(((parseFloat(values.amount) || 0) * (parseFloat(values.tdsRate) || 0)) / 100)}
                          </span>
                        </div>
                        <div className="tw-flex tw-justify-between tw-text-sm">
                          <span className="tw-text-gray-500">Net Payable:</span>
                          <span className="tw-font-semibold tw-text-gray-800">
                            {formatCurrency((parseFloat(values.amount) || 0) - ((parseFloat(values.amount) || 0) * (parseFloat(values.tdsRate) || 0)) / 100)}
                          </span>
                        </div>
                      </>
                    )}

                    <div className="tw-flex tw-justify-between tw-text-sm">
                      <span className="tw-text-gray-500">Amount used for Payments:</span>
                      <span className="tw-font-semibold tw-text-gray-800">
                        {formatCurrency(totalBillPayment)}
                      </span>
                    </div>

                    <hr className="tw-border-gray-100" />
                    <div className="tw-flex tw-justify-between tw-text-sm">
                      <span className="tw-text-gray-500">Amount in Excess:</span>
                      <span className={`tw-font-semibold ${(parseFloat(values.amount) || 0) - totalBillPayment >= 0 ? 'tw-text-emerald-600' : 'tw-text-red-600'}`}>
                        {formatCurrency((parseFloat(values.amount) || 0) - totalBillPayment)}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="tw-mt-6 tw-space-y-2">
                    <button
                      type="submit"
                      disabled={updateVendorPaymentLoading}
                      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                      {updateVendorPaymentLoading ? (
                        <>
                          <Icon icon="mdi:loading" className="tw-animate-spin" />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:check" />
                          Update Payment
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/society-portal' + routes.SOCIETY_PORTAL_VENDOR_PAYMENTS)}
                      className="tw-w-full tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default EditVendorPayment;
